import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Building2, Check, ChevronDown } from 'lucide-react'
import { usePermissions } from './PermissionsContext'

/**
 * Switch the active brand, which filters the lists, the dashboard and the map.
 *
 * "All brands" comes first and is the default. The choice is remembered per user on this browser
 * (see PermissionsContext). Hidden entirely when the user has one brand or none: a picker with a
 * single option is noise, and brand scoping is invisible to most operators.
 */
const ALL_BRANDS = 'All brands'

export function BrandSwitcher() {
  const { brands, activeBrandId, setActiveBrand } = usePermissions()

  if (brands.length <= 1) return null

  const active = brands.find((b) => b.id === activeBrandId)
  const itemClass =
    'flex cursor-pointer items-center gap-sm rounded-md px-md py-sm text-sm text-fg outline-none transition-colors hover:bg-bg-hover'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-xs rounded-md px-sm py-xs text-sm text-header-muted transition-colors hover:bg-header-hover hover:text-header-fg"
        >
          <Building2 size={14} aria-hidden="true" />
          <span className="max-w-24 truncate">{active?.name ?? ALL_BRANDS}</span>
          <ChevronDown size={12} aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={8}
          className="min-w-44 rounded-lg border border-card-border bg-card p-xs shadow-lg"
          style={{ zIndex: 'var(--z-dropdown)' }}
        >
          <DropdownMenu.Item className={itemClass} onSelect={() => setActiveBrand(null)}>
            <Check size={14} aria-hidden="true" className={active ? 'opacity-0' : 'opacity-100'} />
            {ALL_BRANDS}
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-xs h-px bg-card-border" />
          {brands.map((brand) => (
            <DropdownMenu.Item
              key={brand.id}
              className={itemClass}
              onSelect={() => setActiveBrand(brand.id)}
            >
              <Check
                size={14}
                aria-hidden="true"
                className={brand.id === activeBrandId ? 'opacity-100' : 'opacity-0'}
              />
              {brand.name}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
